import { Message } from 'services/conversations/types';
import {
  CampaignType,
  Channels,
  ConversationStatus
} from 'generated-graphql/types';
import {
  checkLastProspectMessageIsWithin24Hours,
  checkProspectHasMessaged,
  isLastMessageRestartConversation
} from '../utils';
import { ConversationItem } from '../types/ConversationItem';

export enum ConversationActionState {
  HIDDEN = 'HIDDEN',
  WHATSAPP_RESTARTING = 'WHATSAPP_RESTARTING',
  DIFFERENT_CONVERSATION = 'DIFFERENT_CONVERSATION',
  WHATSAPP_WAITING_FOR_REPLY = 'WHATSAPP_WAITING_FOR_REPLY',
  WHATSAPP_CAN_RESTART = 'WHATSAPP_CAN_RESTART',
  HUMAN_TAKEOVER = 'HUMAN_TAKEOVER',
  AGENT_CONTROL_DISABLED = 'AGENT_CONTROL_DISABLED',
  AGENT_CONTROL = 'AGENT_CONTROL',
  HUMAN_TAKEOVER_WITH_NO_HANDOVER = 'HUMAN_TAKEOVER_WITH_NO_HANDOVER'
}

type Props = {
  isDifferentConversationOngoing: boolean;
  conversation: ConversationItem | undefined;
  channel: Channels | undefined | null;
  campaignType: CampaignType | undefined | null;
  messageList: Message[];
};

const postiveStatuses = [
  ConversationStatus.Completed,
  ConversationStatus.CompletedNotInterested,
  ConversationStatus.CompletedAwaitingCall,
  ConversationStatus.CompletedCustomRequest,
  ConversationStatus.CompletedMeetingRequested,
  ConversationStatus.CompletedRejected,
  ConversationStatus.Ongoing
];

export const useConversationActionState = ({
  isDifferentConversationOngoing,
  conversation,
  channel,
  campaignType,
  messageList
}: Props) => {
  if (!conversation) {
    return ConversationActionState.HIDDEN;
  }

  const shouldShowActions = postiveStatuses.includes(conversation.status);

  if (!shouldShowActions) {
    return ConversationActionState.HIDDEN;
  }

  const lastMessageRestartConversation =
    isLastMessageRestartConversation(messageList);

  if (lastMessageRestartConversation) {
    return ConversationActionState.WHATSAPP_RESTARTING;
  }

  if (isDifferentConversationOngoing) {
    return ConversationActionState.DIFFERENT_CONVERSATION;
  }

  const hasProspectMessaged = checkProspectHasMessaged(messageList);

  if (channel === Channels.Whatsapp && !hasProspectMessaged) {
    return ConversationActionState.WHATSAPP_WAITING_FOR_REPLY;
  }

  const lastProspectMessageWithin24Hours =
    checkLastProspectMessageIsWithin24Hours(messageList);

  if (
    channel === Channels.Whatsapp &&
    !lastProspectMessageWithin24Hours &&
    !isDifferentConversationOngoing
  ) {
    return ConversationActionState.WHATSAPP_CAN_RESTART;
  }

  if (conversation.isHumanTakeover) {
    if (campaignType === CampaignType.HumanOutreach) {
      return ConversationActionState.HUMAN_TAKEOVER_WITH_NO_HANDOVER;
    }

    return ConversationActionState.HUMAN_TAKEOVER;
  }

  if (
    isDifferentConversationOngoing ||
    (channel === Channels.Whatsapp &&
      hasProspectMessaged &&
      !lastProspectMessageWithin24Hours)
  ) {
    return ConversationActionState.AGENT_CONTROL_DISABLED;
  }

  return ConversationActionState.AGENT_CONTROL;
};
